/**
 * /auth/refresh-step-up — REQ-AUTH-005 step-up re-challenge.
 *
 * Three-step flow (now shared with the global step-up modal via
 * useStepUpChallenge — FF-UI-2):
 *   1. User completes a Supabase MFA challenge (WebAuthn or TOTP).
 *   2. Frontend POSTs to /api/v1/auth/refresh-step-up — backend confirms
 *      aal2 + freshness, stamps app_metadata.mfa_challenge_at via the
 *      Supabase admin API.
 *   3. Frontend calls supabase.auth.refreshSession() to pick up the new
 *      JWT with the stamped claim, then navigates to `next` (or back).
 *
 * Without step 2, the Supabase aal2 token works for Supabase-side gates but
 * NEVER lifts the Elreign-side step-up gate (which keys on the stamped
 * mfa_challenge_at claim).
 *
 * Who still lands here (vs the global modal): pre-emptive gates that fire
 * BEFORE any mutation exists to replay — the superadmin layout's stale-claim
 * redirect, the admin dashboard "Verify to reveal" CTA for step-up-gated
 * reads, and the backend's `redirectTo` hint for deep links. Mutation
 * failures no longer navigate here; the StepUpModalHost handles those in
 * place and replays the mutation.
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AuthLayout } from '../../components/layouts/auth-layout';
import { useStepUpChallenge } from '../../lib/use-step-up-challenge';

/** Search params shape — all optional, all are caller-supplied strings. */
interface StepUpSearch {
  next?: string;
  return?: string;
  factor?: string;
}

export const Route = createFileRoute('/auth/refresh-step-up')({
  component: RefreshStepUpPage,
  // Accept `next` (canonical), plus `return` + `factor` for compatibility
  // with the SuperAdmin layout guard which predates this route's validator.
  validateSearch: (search: Record<string, unknown>): StepUpSearch => {
    const out: StepUpSearch = {};
    if (typeof search['next'] === 'string') out.next = search['next'];
    if (typeof search['return'] === 'string') out.return = search['return'];
    if (typeof search['factor'] === 'string') out.factor = search['factor'];
    return out;
  },
});

function RefreshStepUpPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  // `next` is canonical; `return` is the SuperAdmin layout guard's older
  // name for the same thing.
  const destination = search.next ?? search.return;

  const [otp, setOtp] = useState('');
  const {
    factor,
    showWebAuthnBranch,
    verifying,
    error,
    codeLabel,
    emailCodePending,
    notice,
    verifyTotp,
    verifyPasskey,
  } = useStepUpChallenge();

  /** Post-verify: return the user to where they came from (or back). */
  function leave() {
    if (destination && destination.startsWith('/')) {
      void navigate({ to: destination as never });
    } else {
      window.history.back();
    }
  }

  async function onPasskeySign() {
    const ok = await verifyPasskey();
    if (ok) leave();
  }

  async function onOtpSubmit(e: React.FormEvent) {
    e.preventDefault();
    // While the extra email code is outstanding this same field carries it (see
    // use-step-up-challenge.ts). Clear the box afterwards so the user is not
    // typing their authenticator code on top of the emailed one.
    const wasEmailStep = emailCodePending;
    const ok = await verifyTotp(otp);
    if (wasEmailStep) setOtp('');
    if (ok) leave();
  }

  return (
    <AuthLayout
      eyebrow="Security check: step-up authentication"
      heading="Confirm it's you."
      sub={
        <>
          This action needs a fresh MFA challenge. Your session is valid; this
          is an additional confirmation for high-trust actions.
        </>
      }
      stepUp
    >
      <form
        onSubmit={showWebAuthnBranch ? (e) => e.preventDefault() : (e) => void onOtpSubmit(e)}
        style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
      >
        {/* Disclosure when the extra email code could not be delivered and was
            therefore skipped. Never silent. */}
        {notice && (
          <div
            role="status"
            aria-live="polite"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--hairline)',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 13,
              color: 'var(--ink-2)',
            }}
          >
            {notice}
          </div>
        )}

        {error && (
          <div
            role="alert"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--hairline)',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 13,
              color: 'var(--ink-2)',
            }}
          >
            {error}
          </div>
        )}

        {showWebAuthnBranch ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0 }}>
              Use your registered passkey (Face ID, Touch ID, Windows Hello, or
              security key) to confirm this action.
            </p>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => void onPasskeySign()}
                disabled={verifying}
                style={{
                  flex: 1,
                  padding: '13px 18px',
                  borderRadius: 10,
                  border: 'none',
                  background: verifying ? 'var(--surface-2)' : 'var(--accent-400)',
                  color: verifying ? 'var(--ink-4)' : 'var(--primary-900)',
                  fontFamily: 'var(--sans)',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: verifying ? 'not-allowed' : 'pointer',
                  transition: 'all 200ms var(--ease-cushion)',
                }}
              >
                {verifying ? 'Verifying…' : 'Sign with passkey'}
              </button>
              <button
                type="button"
                onClick={() => window.history.back()}
                style={{
                  padding: '13px 18px',
                  borderRadius: 10,
                  border: '1px solid var(--hairline)',
                  background: 'transparent',
                  color: 'var(--ink-2)',
                  fontFamily: 'var(--sans)',
                  fontSize: 15,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>
                {codeLabel}
              </span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000 000"
                autoFocus
                autoComplete="one-time-code"
                aria-label="One-time password"
                className="mono"
                style={{
                  width: '100%',
                  padding: '16px 18px',
                  borderRadius: 9,
                  border: '1px solid var(--accent-300)',
                  background: 'var(--accent-50)',
                  color: 'var(--ink)',
                  fontFamily: 'var(--mono)',
                  fontSize: 32,
                  letterSpacing: '0.25em',
                  textAlign: 'center',
                  boxSizing: 'border-box',
                }}
              />
            </label>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="submit"
                // `!factor` must NOT block the email step: a passkey-only
                // user has no TOTP factor and still has to submit the code.
                disabled={otp.length !== 6 || verifying || (!factor && !emailCodePending)}
                style={{
                  flex: 1,
                  padding: '13px 18px',
                  borderRadius: 10,
                  border: 'none',
                  background: otp.length === 6 && !verifying ? 'var(--accent-400)' : 'var(--surface-2)',
                  color: otp.length === 6 && !verifying ? 'var(--primary-900)' : 'var(--ink-4)',
                  fontFamily: 'var(--sans)',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: otp.length === 6 && !verifying ? 'pointer' : 'not-allowed',
                  transition: 'all 200ms var(--ease-cushion)',
                }}
              >
                {verifying ? 'Verifying…' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={() => window.history.back()}
                style={{
                  padding: '13px 18px',
                  borderRadius: 10,
                  border: '1px solid var(--hairline)',
                  background: 'transparent',
                  color: 'var(--ink-2)',
                  fontFamily: 'var(--sans)',
                  fontSize: 15,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>

            {!factor && !emailCodePending && (
              <p style={{ fontSize: 12.5, color: 'var(--ink-4)', marginTop: 4, lineHeight: 1.55 }}>
                No MFA factor is enrolled on this account.{' '}
                <a href="/auth/mfa-enroll" style={{ color: 'var(--primary-700)' }}>
                  Enrol an authenticator
                </a>
                {' '}before continuing.
              </p>
            )}
          </>
        )}
      </form>

      <p style={{ fontSize: 12.5, color: 'var(--ink-4)', marginTop: 20, lineHeight: 1.55 }}>
        Step-up challenges expire after 10 minutes. If you cancel, you will return
        to the previous screen without completing the high-trust action.
      </p>
    </AuthLayout>
  );
}
